/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef} from 'react';
import debounce from 'lodash.debounce';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {isScDevice} from '@ion/core/src/services/ArcGISUtils';
import Device from '@ion/core/src/services/Device';
import {SCDeviceType} from '@ion/core/src/types/entities';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import {
  applyEditsToLayer,
  createGraphics,
  getFeatures as getDeviceEntity,
  getItemQuery,
  getLayerEffectQuery,
  layerExists,
  loadDataUpdates,
  noneSelectedQuery,
  onLayerClickEvent,
  removeAllFeatures,
  showSCDevicePopup
} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {
  createCircleSymbol
} from '@ion/dls/src/maps/symbols/CircleSymbol';
import {
  createDeviceSymbol
} from '@ion/dls/src/maps/symbols/DeviceSymbol';
import {
  createSignalSymbol
} from '@ion/dls/src/maps/symbols/SignalSymbol';
import {useMedia} from '../../hooks/useMedia';
import {BaseMapEnum} from '../../types/types';
import {mapViewGoToOptions, SC_DEVICE_FIELDS} from '../constants';
import {initializeEsriModules} from '../helpers/initializeEsriModules';
import {getLabelClassQuery, getLayerLabelClass} from '../helpers/LabelUtils';
import {getLayerIndex} from '../helpers/MapViewUtils';
import {useFeatureLayerEffect} from '../hooks/useAssetFeatureLayerEffect';
import {
  ASSET_LAYER_ID,
  AssetLayerProps,
  CLUSTER_LAYER_ID,
  VIEW_ZOOM
} from './AssetLayer';
import {isSCDeviceEnabled} from './LayerChooser';

// Defines the props definitions for the TripHistoryLayer
type SCDeviceProps = {
  map?: __esri.Map;
  view?: __esri.MapView;
  layers?: string[];
  assetLayerProps?: AssetLayerProps;
};

// Defines the props definitions for the Add / Update method
type AddUpdateProps = {
  layer: __esri.Feature;
  data: SCDeviceType[];
  create: Function;
  addUpdate: Function;
};

// Defines label class props
type LabelClassProps = {
  labelContent: string,
  basemap: BaseMapEnum,
  selectedDevice: SCDeviceType,
  isDeviceRenderer: boolean
}

/**
 * Defines the unique identifier for the trip SC Device layer which is used as globally
 * as the layer id.
 */
export const SC_DEVICE_LAYER_ID  = 'supplyChainDevice';

/**
 * Default SC Devices Auto updates Interval (12 seconds)
 */
export const AUTO_UPDATE_INTERVAL = 12000;

// Defines a flag for client side renderer
const isClientSideRenderer = true;

// Defines the identifier property for Device layer
const identifierProperty = 'id';

// Defines the identifier type for Device layer
const identifierType = 'isScDevice';

/**
 * Defines a utility to return the renderer of the layer
 */
const getRenderer = () => {
  return {
    type   : 'simple',
    symbol : {
      type : 'cim',
      data : {
        type   : 'CIMSymbolReference',
        symbol : {
          type         : 'CIMPointSymbol',
          symbolLayers : [
            createSignalSymbol(),
            createDeviceSymbol(),
            createCircleSymbol()
          ]
        }
      }
    }
  };
};

/**
 * Defines a utility to Add / Update graphics to the layer
 */
const AddUpdateGraphics = ({layer, data, create, addUpdate}: AddUpdateProps) => {
  if (!layer) {
    data?.length && create(data);
  } else {
    addUpdate(data);
  }
};

/**
 * @SCDeviceLayer
 *
 * Defines the SCDeviceLayer which manages rendering a layer containing SC Devices
 * within a DLS Map.
 *
 *
 * ```typescript
 *
 *   import {ResponsiveBaseMapLayer, SCDeviceLayer} from './layers';
 *   const components: any = [ResponsiveBaseMapLayer]
 *   components.push(SCDeviceLayer);
 *
 * ```
 */
export const SCDeviceLayer = ({
  map,
  view,
  layers,
  assetLayerProps: {
    isLargeFleetMode,
    crumbs,
    setCrumbs,
    scDevices,
    selectedDevice,
    globalGroupId,
    isMapSelection,
    loadSCDevices,
    onDeviceSelected,
    setSelectedDevice,
    isScDevicesLabelActive,
    scDevicesLabelContent,
    scDevicesUpdates,
    setDeviceDetailView,
    isHistoryAccessible,
    setFindButtonStatus,
    assetFindNearbyClick,
    isFindNearbyPopup,
    onFindNearyBy,
    onFindNearbyPopupStatusChange,
    onFindNearbyBreadcrumbBackClick,
    onFindButtonStateChange,
    findLabel,
    findNearbyIsActive,
    loadSCDevicesUpdates,
    onResetSCDevices,
    onDeviceReports,
    basemap
  } = {} as AssetLayerProps
}: SCDeviceProps) => {
  const layerRef            = useRef(null);
  const timerRef            = useRef(null);
  const clickRef            = useRef(null);
  const graphicRef          = useRef(null);
  const isLargeFleetModeRef = useRef(isLargeFleetMode);
  const globalGroupIdRef    = useRef(globalGroupId);
  const layersRef           = useRef(layers);
  const crumbsRef           = useRef(crumbs);
  const selectedDeviceRef   = useRef(selectedDevice);
  const isMounted           = useHasMounted();
  const {isPhone}           = useMedia();
  const isPhoneRef          = useRef(isPhone);
  const {
    createSelectionEffect,
    setLayerHighlighter,
    removeHighlight,
    highlightItem
  } = useFeatureLayerEffect();

  useEffect(() => {
    return () => {
      stopAutoUpdates?.();
      onResetSCDevices?.();
      removeHighlight(layerRef?.current);
      clickRef?.current?.remove('click', onClickViewEvent);
    };
  }, []);

  useEffect(() => {
    if (globalGroupId && globalGroupIdRef.current !== globalGroupId) {
      globalGroupIdRef.current = globalGroupId;
      removeHighlight(layerRef?.current);
    }
  }, [globalGroupId]);

  useEffect(() => {
    selectedDeviceRef.current = selectedDevice;
    removeHighlight(layerRef?.current);
    !findNearbyIsActive && highlightDeviceLayer(selectedDevice);
  }, [selectedDevice, findNearbyIsActive]);

  useEffect(() => {
    if (isMounted) {
      loadSCDevices();
    }
  }, [isMounted, globalGroupId]);

  // Add Initial load data
  useEffect(() => {
    const params = {
      layer     : layerRef.current,
      data      : scDevices,
      create    : createSCDeviceLayer,
      addUpdate : addSCDevices
    };
    AddUpdateGraphics(params);
  }, [scDevices]);

  // Add data updates
  useEffect(() => {
    if (scDevicesUpdates?.length) {
      const params = {
        layer     : layerRef.current,
        data      : scDevicesUpdates,
        create    : createSCDeviceLayer,
        addUpdate : updateSCDevices
      };
      AddUpdateGraphics(params);
    }
  }, [scDevicesUpdates]);

  // Set Layer visibility
  useEffect(() => {
    layersRef.current = layers;
    isLargeFleetModeRef.current = isLargeFleetMode;
    if (layerRef.current) {
      layerRef.current.visible = !isLargeFleetMode && isSCDeviceEnabled(layers);
    }
    if (isSCDeviceEnabled(layers) && !isLargeFleetMode && !findNearbyIsActive) {
      startAutoUpdates();
    } else {
      stopAutoUpdates();
    }
  }, [layers, isLargeFleetMode, findNearbyIsActive]);

  // Enable popup based on isPhone
  useEffect(() => {
    isPhoneRef.current = isPhone;
    if (layerRef.current) {
      layerRef.current.popupEnabled = !isPhone;
    }
  }, [isPhone]);

  // set scrumbs data
  useEffect(() => {
    crumbsRef.current = crumbs;
  }, [crumbs]);

  // Zoom the map to selected Device
  useEffect(() => {
    if (selectedDevice && isScDevice(selectedDevice) && !isMapSelection) {
      const {longitude, latitude} = selectedDevice;
      view.popup?.close?.();
      view.goTo({center: [longitude, latitude], zoom: VIEW_ZOOM}, mapViewGoToOptions);
    }
  }, [selectedDevice]);

  // Update label class based on label content
  useEffect(() => {
    if (layerRef.current) {
      const selectedDeviceLabel = !findNearbyIsActive ? selectedDevice : null;
      layerRef.current.labelingInfo = getLabelClass({
        isDeviceRenderer : !!isClientSideRenderer,
        labelContent     : scDevicesLabelContent,
        selectedDevice   : selectedDeviceLabel,
        basemap
      });
    }
  }, [selectedDevice, scDevicesLabelContent, basemap, findNearbyIsActive]);

  // Label visibility
  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.labelsVisible = isScDevicesLabelActive;
    }
  }, [isScDevicesLabelActive]);

  // Defines a utility to highlight selected devices when getting updates
  const highlightSelectedDevice = useCallback((devices: Device[]) => {
    if (devices?.length && selectedDeviceRef.current) {
      const selected = devices.map(getDeviceEntity)?.find(({id}) =>
        id === selectedDeviceRef.current?.id);
      selected && highlightDeviceLayer(selected);
    }
  }, []);

  // Defines a utility to highlight selected item and applying Feature Effect to Device Layer
  const highlightDeviceLayer = useCallback(debounce((device: SCDeviceType) => {
    const deviceLayerRef = layerRef?.current;
    if (deviceLayerRef) {
      if (device && !isLargeFleetModeRef.current) {
        const query = getItemQuery({
          item         : device,
          property     : identifierProperty,
          defaultQuery : noneSelectedQuery
        });
        const where = getLayerEffectQuery({
          selectedItem : device,
          type         : identifierType,
          query
        });
        highlightItem(deviceLayerRef, where);
        createSelectionEffect(deviceLayerRef, where);
      } else if (deviceLayerRef?.featureEffect) {
        deviceLayerRef.featureEffect = null;
      }
    }
  }, 135), []);

  //Defines a utility to return a Label Class
  const getLabelClass = useCallback(({
    labelContent,
    selectedDevice,
    isDeviceRenderer,
    basemap
  }: LabelClassProps) => {
    const where = getLabelClassQuery({
      selectedItem : selectedDevice,
      type         : identifierType,
      property     : identifierProperty
    });
    const labelClass = getLayerLabelClass({
      isRenderer          : isDeviceRenderer,
      defaultColor        : true,
      defaultLabelContent : 'deviceName',
      labelContent,
      basemap,
      where
    });
    return labelClass;
  }, []);

  const onLayerUpdates = useCallback((devices: Device[]) => {
    highlightSelectedDevice(devices);
  }, []);

  /**
   * createSCDeviceLayer helps to create the SC Device Layer
   */
  const createSCDeviceLayer = async(scDevices: Device[]) => {
    if (!layerRef.current) {
      if (!layerExists(map, SC_DEVICE_LAYER_ID)) {
        const {layers}       = map;
        const isVisible      = isSCDeviceEnabled(layersRef.current);
        const [FeatureLayer] = await initializeEsriModules('esri/layers/FeatureLayer');
        const graphics       = await createGraphics(scDevices, SC_DEVICE_FIELDS);
        layerRef.current     = new FeatureLayer({
          title         : SC_DEVICE_LAYER_ID,
          id            : SC_DEVICE_LAYER_ID,
          outFields     : ['*'],
          source        : graphics,
          objectIdField : 'OBJECTID',
          renderer      : getRenderer(),
          fields        : SC_DEVICE_FIELDS,
          labelingInfo  : getLabelClass({
            selectedDevice   : selectedDeviceRef.current,
            labelContent     : scDevicesLabelContent,
            isDeviceRenderer : !!isClientSideRenderer,
            basemap
          }),
          popupEnabled : false
        });

        layers.add(layerRef.current);
        layers.reorder(layerRef.current, getLayerIndex(map, SC_DEVICE_LAYER_ID));
        setLayerHighlighter(layerRef.current, view);
        layerRef.current.visible       = !isLargeFleetMode && isVisible;
        layerRef.current.labelsVisible = isScDevicesLabelActive;
        clickRef.current               = view.on('click', onClickViewEvent);
        isVisible && startAutoUpdates?.();
        selectedDeviceRef.current && highlightDeviceLayer(selectedDeviceRef.current);
      }
    }
  };

  /**
   * addSCDevices helps to add SC Devices
   */
  const addSCDevices = async(scDevices: Device[]) => {
    if (layerRef.current) {
      const graphics = await createGraphics(scDevices, SC_DEVICE_FIELDS);
      removeAllFeatures(layerRef.current).then(() => {
        if (graphics?.length) {
          applyEditsToLayer(
            layerRef.current,
            {addFeatures: graphics},
            () => onLayerUpdates(scDevices)
          );
        }
      });
    }
  };

  /**
   * updateSCDevices helps to update/add SC Devices
   */
  const updateSCDevices = async(scDevicesUpdates: Device[]) => {
    if (layerRef.current) {
      const graphics = await createGraphics(scDevicesUpdates, SC_DEVICE_FIELDS);
      const query    = layerRef?.current?.createQuery();
      query.where    = `deviceIdValue IN (${scDevicesUpdates?.map((device) => `'${device?.getDevice()?.deviceIdValue}'`)})`;
      layerRef.current?.queryFeatures(query).then(({features}) => {
        applyEditsToLayer(
          layerRef.current,
          {deleteFeatures: features, addFeatures: graphics},
          () => onLayerUpdates(scDevicesUpdates)
        );
      });
    }
  };

  /**
   * Start the SC Devices auto updates
   */
  const startAutoUpdates = useCallback(() => {
    if (!timerRef.current && isSCDeviceEnabled(layersRef.current) &&
      !findNearbyIsActive && AUTO_UPDATE_INTERVAL > 0) {
      timerRef.current = setInterval(() => {
        loadDataUpdates(layerRef?.current, 'dbWriteTime', loadSCDevicesUpdates);
      }, AUTO_UPDATE_INTERVAL);
    }
  }, [findNearbyIsActive]);

  /**
   * Stop the SC Devices auto updates
   */
  const stopAutoUpdates = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  /**
   * Show Device popup
   */
  const showPopup = useCallback(device => {
    if (isTrue(device?.attributes?.isScDevice)) {
      showSCDevicePopup({
        isPhone         : isPhoneRef.current,
        crumbs          : crumbsRef.current,
        setBreadCrumbs  : setCrumbs,
        selectedGraphic : device,
        assetFindNearbyClick,
        setFindButtonStatus,
        isHistoryAccessible,
        setDeviceDetailView,
        setSelectedDevice,
        isFindNearbyPopup,
        onFindNearyBy,
        onFindNearbyPopupStatusChange,
        onFindNearbyBreadcrumbBackClick,
        onFindButtonStateChange,
        findLabel,
        view,
        onDeviceReports
      });
      graphicRef.current = device;
      onDeviceSelected(device?.attributes, true);
    }
  }, [view]);

  /**
   * Provides a utility to catch the event and determine
   * if the click is for selects an Device or just clicks on the view
   */
  const onClickViewEvent = useCallback(debounce(({x, y}) => {
    onLayerClickEvent({
      layerId        : [ASSET_LAYER_ID, CLUSTER_LAYER_ID, SC_DEVICE_LAYER_ID],
      callback       : showPopup,
      includeAny     : true,
      includeResults : true,
      view,
      x,
      y
    });
  }, 130), []);

  return null;
};
